import { useState, useEffect } from "react";
import NectarApp from "./NectarApp";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

const G   = "#0077B5";
const BG  = "#F3F2EF";
const CARD= "#FFFFFF";
const B   = "#E1E9EE";
const GRN = "#057642";
const RED = "#C0392B";
const NAV = "#0f2744";

function ah(token) {
  return { "X-Admin-Key": token, "Content-Type": "application/json" };
}

function getAdminSubroute() {
  const hash = window.location.hash;
  if (hash.startsWith("#/admin/impersonate/")) return { page: "impersonate", id: hash.split("/")[3] };
  if (hash.startsWith("#/admin/users/"))       return { page: "user-detail", id: hash.split("/")[3] };
  if (hash.startsWith("#/admin/users"))        return { page: "users" };
  if (hash.startsWith("#/admin/errors"))       return { page: "errors" };
  if (hash.startsWith("#/admin/dashboard"))    return { page: "dashboard" };
  return { page: "login" };
}

// ─── Tiny shared components ────────────────────────────────────────────────────
function StatCard({ label, value, color = "#1A1A1A" }) {
  return (
    <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "18px 20px", flex: 1, minWidth: 140 }}>
      <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{value ?? "—"}</div>
    </div>
  );
}

function PlanBadge({ plan }) {
  const p = (plan || "trial").toLowerCase();
  const c = { trial: ["#FEF3C7","#92400E","#FCD34D"], free: ["#EBF5FB","#005885","#93C5FD"], presence: ["#F0FAF4","#057642","#86EFAC"], authority: ["#F5F3FF","#6D28D9","#C4B5FD"], team: ["#F5F3FF","#6D28D9","#C4B5FD"], agency: ["#F5F3FF","#6D28D9","#C4B5FD"] }[p] || ["#FEF3C7","#92400E","#FCD34D"];
  return <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, letterSpacing: "0.08em", padding: "2px 8px", borderRadius: 4, background: c[0], color: c[1], border: `1px solid ${c[2]}` }}>{p.toUpperCase()}</span>;
}

function AccountStatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let bg, color, border;
  if (s === "active")    { bg = "#F0FAF4"; color = "#057642"; border = "#86EFAC"; }
  else if (s === "trialing") { bg = "#EBF5FB"; color = "#005885"; border = "#93C5FD"; }
  else if (["churned","cancelled","expired"].includes(s)) { bg = "#FEF2F2"; color = RED; border = "#FCA5A5"; }
  else { bg = "#F3F4F6"; color = "#6B7280"; border = "#D1D5DB"; }
  return (
    <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, letterSpacing: "0.08em", padding: "2px 8px", borderRadius: 4, background: bg, color, border: `1px solid ${border}` }}>
      {s ? s.toUpperCase() : "UNKNOWN"}
    </span>
  );
}

function TokenStatusBadge({ expiresAt, needsReauth }) {
  if (needsReauth) return <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#FEF2F2", color: RED, border: "1px solid #FCA5A5" }}>NEEDS REAUTH</span>;
  if (!expiresAt) return <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#F3F4F6", color: "#6B7280", border: "1px solid #D1D5DB" }}>NO TOKEN</span>;
  try {
    const exp = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.floor((exp - now) / 86400000);
    if (daysLeft < 0) return <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#FEF2F2", color: RED, border: "1px solid #FCA5A5" }}>EXPIRED</span>;
    if (daysLeft <= 30) return <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#FFFBEB", color: "#92400E", border: "1px solid #FCD34D" }}>EXPIRING SOON</span>;
    return <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#F0FAF4", color: GRN, border: "1px solid #86EFAC" }}>HEALTHY</span>;
  } catch { return null; }
}

function Avatar({ name, img, size = 36 }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  if (img && !err) return <img src={img} alt={name} onError={() => setErr(true)} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />;
  return <div style={{ width: size, height: size, borderRadius: "50%", background: G, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: size * 0.38, fontWeight: 700, flexShrink: 0 }}>{initials}</div>;
}

function AdminNav({ page, onNavigate, onLogout }) {
  return (
    <div style={{ background: NAV, padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 52, position: "sticky", top: 0, zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>
          BrandHives <span style={{ fontSize: 11, fontWeight: 600, background: "#dc3545", color: "#fff", borderRadius: 4, padding: "2px 7px", marginLeft: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", letterSpacing: "0.1em" }}>ADMIN</span>
        </div>
        <div style={{ display: "flex", gap: 2, marginLeft: 12 }}>
          {[["dashboard","Dashboard"],["users","Users"],["errors","Error Log"]].map(([p, label]) => (
            <button key={p} onClick={() => onNavigate(p)} style={{ background: "transparent", color: page === p ? "#fff" : "rgba(255,255,255,0.5)", border: "none", borderBottom: page === p ? "2px solid #fff" : "2px solid transparent", borderRadius: 0, padding: "0 14px", height: 52, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: page === p ? 600 : 400, cursor: "pointer" }}>{label}</button>
          ))}
        </div>
      </div>
      <button onClick={onLogout} style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.75)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "6px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer" }}>Logout</button>
    </div>
  );
}

// ─── Main admin shell ──────────────────────────────────────────────────────────
export default function NectarAdmin() {
  const [subroute, setSubroute] = useState(getAdminSubroute);
  const [token, setToken]       = useState(() => sessionStorage.getItem("nectar_admin_token") || "");
  const [authed, setAuthed]     = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const handler = () => setSubroute(getAdminSubroute());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  useEffect(() => {
    const tok = sessionStorage.getItem("nectar_admin_token") || "";
    if (!tok) { setChecking(false); return; }
    fetch(`${API_BASE}/app/api/admin/verify`, { headers: { "X-Admin-Key": tok } })
      .then(r => { if (r.ok) { setAuthed(true); setToken(tok); } else { sessionStorage.removeItem("nectar_admin_token"); } })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []); // eslint-disable-line

  function navigate(page, id) {
    if (page === "dashboard")    window.location.hash = "#/admin/dashboard";
    else if (page === "users")   window.location.hash = "#/admin/users";
    else if (page === "errors")  window.location.hash = "#/admin/errors";
    else if (page === "user-detail")   window.location.hash = `#/admin/users/${id}`;
    else if (page === "impersonate")   window.location.hash = `#/admin/impersonate/${id}`;
    setSubroute(getAdminSubroute());
  }

  function logout() {
    sessionStorage.removeItem("nectar_admin_token");
    setAuthed(false); setToken("");
    window.location.hash = "#/admin";
  }

  if (checking) return <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, color: G }}>BrandHives Admin</div>;

  if (!authed || subroute.page === "login") return <AdminLogin onSuccess={tok => { setToken(tok); setAuthed(true); navigate("dashboard"); }} />;

  if (subroute.page === "impersonate") return <AdminImpersonate userId={subroute.id} token={token} onExit={() => navigate("user-detail", subroute.id)} />;

  return (
    <div style={{ background: BG, minHeight: "100vh", fontFamily: "'Playfair Display',Georgia,serif", color: "#1A1A1A" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} body{background:#F3F2EF;-webkit-font-smoothing:antialiased;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} .mono{font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} .ar:hover{background:#F8FAFB !important;} input,select{outline:none;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} @keyframes adminFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} .af{animation:adminFade 0.3s ease forwards;}`}</style>
      <AdminNav page={subroute.page} onNavigate={navigate} onLogout={logout} />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {subroute.page === "dashboard"   && <AdminDashboard token={token} onNavigate={navigate} />}
        {subroute.page === "users"       && <AdminUsers     token={token} onNavigate={navigate} />}
        {subroute.page === "user-detail" && <AdminUserDetail userId={subroute.id} token={token} onNavigate={navigate} />}
        {subroute.page === "errors"      && <AdminErrorLog  token={token} />}
      </div>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function AdminLogin({ onSuccess }) {
  const [pw, setPw]       = useState("");
  const [loading, setL]   = useState(false);
  const [err, setErr]     = useState("");

  const doLogin = async () => {
    if (!pw) return;
    setL(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pw }) });
      const d = await r.json();
      if (r.ok && d.success) { sessionStorage.setItem("nectar_admin_token", d.token); onSuccess(d.token); }
      else setErr("Invalid password");
    } catch { setErr("Connection error"); }
    setL(false);
  };

  return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display',Georgia,serif" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;}`}</style>
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 16, padding: "40px 36px", width: "100%", maxWidth: 380, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: G, marginBottom: 8 }}>BrandHives</div>
          <span style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, letterSpacing: "0.12em", background: "#dc3545", color: "#fff", borderRadius: 4, padding: "2px 8px" }}>ADMIN</span>
        </div>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && doLogin()} placeholder="Admin password"
          style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "12px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, marginBottom: 12, color: "#1A1A1A" }} />
        {err && <div style={{ color: RED, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button onClick={doLogin} disabled={!pw || loading}
          style={{ width: "100%", background: pw && !loading ? G : "#EBF5FB", color: pw && !loading ? "#fff" : "#999", border: "none", borderRadius: 10, padding: "13px", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 17, cursor: pw && !loading ? "pointer" : "not-allowed" }}>
          {loading ? "Verifying…" : "Login →"}
        </button>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function AdminDashboard({ token, onNavigate }) {
  const [overview, setOverview] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [hcResult, setHcResult] = useState(null);
  const [hcLoading, setHcLoading] = useState(false);
  const h = ah(token);

  useEffect(() => {
    fetch(`${API_BASE}/app/api/admin/overview`, { headers: h }).then(r => r.ok ? r.json() : null).then(setOverview).catch(() => {});
    fetch(`${API_BASE}/app/api/admin/users`, { headers: h }).then(r => r.ok ? r.json() : []).then(rows => setRecentUsers(rows.slice(0, 10))).catch(() => {});
  }, []); // eslint-disable-line

  const runSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/run-sync`, { headers: h });
      const d = await r.json();
      setSyncResult(d.exit_code === 0 ? "Sync complete ✓" : `Sync failed: ${(d.stderr || "").slice(0, 80) || "unknown error"}`);
    } catch { setSyncResult("Request failed"); }
    setSyncing(false);
  };

  const runHealthCheck = async () => {
    setHcLoading(true); setHcResult(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/system-health-check`, { method: "POST", headers: h });
      const d = await r.json();
      setHcResult(d);
    } catch { setHcResult({ error: "Request failed" }); }
    setHcLoading(false);
  };

  const TH = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", padding: "8px 12px", textAlign: "left", borderBottom: `1px solid ${B}`, fontWeight: 500 };
  const TD = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, padding: "10px 12px", borderBottom: `1px solid ${B}`, color: "#1A1A1A" };
  const relDate = d => { if (!d) return "—"; const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return diff === 0 ? "Today" : diff === 1 ? "Yesterday" : `${diff}d ago`; };

  const cards = overview ? [
    { label: "Total Users",      value: overview.total_users },
    { label: "Active Trials",    value: overview.active_trials,      color: "#92400E" },
    { label: "Paid Users",       value: overview.paid_users,         color: GRN },
    { label: "Posts This Week",  value: overview.posts_this_week },
    { label: "Posts Failed",     value: overview.posts_failed,       color: overview.posts_failed > 0 ? RED : "#1A1A1A" },
    { label: "Articles in DB",   value: overview.articles_in_db },
  ] : [];

  return (
    <div className="af">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
        {cards.map((c, i) => <StatCard key={i} label={c.label} value={c.value} color={c.color} />)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 24, alignItems: "start" }}>
        {/* Recent users */}
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${B}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18 }}>Recent Users</div>
            <button onClick={() => onNavigate("users")} style={{ background: "none", border: "none", color: G, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13 }}>View all →</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["","Name / Email","Plan","Posts","Joined"].map((h,i) => <th key={i} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {recentUsers.map(u => (
                <tr key={u.id} className="ar" style={{ cursor: "pointer" }} onClick={() => onNavigate("user-detail", u.id)}>
                  <td style={{ ...TD, width: 40, paddingRight: 4 }}><Avatar name={u.linkedin_name || u.email} img={u.linkedin_profile_image} size={28} /></td>
                  <td style={TD}><div style={{ fontWeight: 500 }}>{u.linkedin_name || "—"}</div><div style={{ color: "#999", fontSize: 12 }}>{u.email}</div></td>
                  <td style={TD}><PlanBadge plan={u.plan} /></td>
                  <td style={{ ...TD, color: "#666" }}>{u.post_count || 0}</td>
                  <td style={{ ...TD, color: "#999" }}>{relDate(u.created_at)}</td>
                </tr>
              ))}
              {!recentUsers.length && <tr><td colSpan={5} style={{ ...TD, textAlign: "center", color: "#999", padding: 32 }}>No users yet</td></tr>}
            </tbody>
          </table>
        </div>

        {/* System status */}
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "20px" }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, marginBottom: 16 }}>System Status</div>
          {overview ? (
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                ["Last article sync", overview.last_sync ? new Date(overview.last_sync).toLocaleDateString("en-CA") : "—"],
                ["Posts published total", overview.posts_published_total],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#666" }}>{k}</span>
                  <span style={{ fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#666" }}>Missing LinkedIn</span>
                <span style={{ fontWeight: 600, color: overview.users_missing_linkedin > 0 ? RED : GRN }}>{overview.users_missing_linkedin > 0 ? `⚠ ${overview.users_missing_linkedin}` : "✓ None"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#666" }}>Posts failed</span>
                <span style={{ fontWeight: 600, color: overview.posts_failed > 0 ? RED : GRN }}>{overview.posts_failed > 0 ? `⚠ ${overview.posts_failed}` : "✓ 0"}</span>
              </div>
              <button onClick={runSync} disabled={syncing}
                style={{ marginTop: 8, background: G, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: syncing ? "not-allowed" : "pointer", opacity: syncing ? 0.7 : 1, width: "100%" }}>
                {syncing ? "Running sync…" : "Run Article Sync"}
              </button>
              {syncResult && <div style={{ fontSize: 12, color: syncResult.includes("complete") ? GRN : RED, marginTop: 4 }}>{syncResult}</div>}
              <button onClick={runHealthCheck} disabled={hcLoading}
                style={{ marginTop: 8, background: hcLoading ? "#888" : "#7C3AED", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: hcLoading ? "not-allowed" : "pointer", opacity: hcLoading ? 0.7 : 1, width: "100%" }}>
                {hcLoading ? "Running health check…" : "Run Health Check"}
              </button>
              {hcResult && !hcResult.error && (
                <div style={{ marginTop: 12, fontSize: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: hcResult.summary?.score === hcResult.summary?.total ? GRN : hcResult.summary?.score >= hcResult.summary?.total * 0.8 ? "#B45309" : RED }}>
                    {hcResult.summary?.score}/{hcResult.summary?.total} checks passing
                    {hcResult.email_sent && <span style={{ marginLeft: 8, fontWeight: 400, color: "#999" }}>· email sent</span>}
                  </div>
                  {(hcResult.sections || []).map(sec => {
                    const allPass = sec.checks.every(c => c.status === "pass");
                    const anyFail = sec.checks.some(c => c.status === "fail");
                    const secColor = anyFail ? RED : allPass ? GRN : "#B45309";
                    return (
                      <div key={sec.name} style={{ marginBottom: 8, padding: "8px 10px", background: "#F9F9F9", borderRadius: 6, border: `1px solid ${anyFail ? "#FECACA" : allPass ? "#BBF7D0" : "#FDE68A"}` }}>
                        <div style={{ fontWeight: 600, color: secColor, marginBottom: 4 }}>{sec.name}</div>
                        {sec.checks.map(c => (
                          <div key={c.name} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: c.status === "pass" ? "#1A1A1A" : c.status === "fail" ? RED : "#B45309" }}>
                            <span>{c.status === "pass" ? "✓" : c.status === "warn" ? "⚠" : "✗"} {c.name}</span>
                            {c.detail && <span style={{ color: "#666", maxWidth: 160, textAlign: "right", fontSize: 11 }}>{c.detail}</span>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
              {hcResult?.error && <div style={{ fontSize: 12, color: RED, marginTop: 4 }}>{hcResult.error}</div>}
            </div>
          ) : <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#999" }}>Loading…</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Users list ───────────────────────────────────────────────────────────────
function AdminUsers({ token, onNavigate }) {
  const [users, setUsers]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortCol, setSort]  = useState("created_at");
  const [sortDir, setDir]   = useState(-1);
  const h = ah(token);

  useEffect(() => {
    fetch(`${API_BASE}/app/api/admin/users`, { headers: h })
      .then(r => r.ok ? r.json() : []).then(d => { setUsers(d); setLoad(false); }).catch(() => setLoad(false));
  }, []); // eslint-disable-line

  const filtered = users
    .filter(u => {
      if (search) { const q = search.toLowerCase(); if (!(u.email?.toLowerCase().includes(q) || u.linkedin_name?.toLowerCase().includes(q))) return false; }
      if (filter === "noli") return !u.linkedin_id;
      if (filter !== "all") return (u.plan || "trial").toLowerCase() === filter;
      return true;
    })
    .sort((a, b) => {
      const av = a[sortCol] ?? "", bv = b[sortCol] ?? "";
      if (typeof av === "number") return sortDir * (av - bv);
      return sortDir * String(av).localeCompare(String(bv));
    });

  const toggleSort = col => { if (sortCol === col) setDir(d => -d); else { setSort(col); setDir(-1); } };

  const TH_S = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", padding: "8px 12px", textAlign: "left", borderBottom: `1px solid ${B}`, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" };
  const TD_S = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, padding: "10px 12px", borderBottom: `1px solid ${B}`, color: "#1A1A1A", verticalAlign: "middle" };
  const sortIndicator = col => sortCol === col ? (sortDir === -1 ? " ↓" : " ↑") : "";

  return (
    <div className="af">
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, flex: 1 }}>Users</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
          style={{ border: `1px solid ${B}`, borderRadius: 8, padding: "8px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, width: 240, outline: "none" }} />
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ border: `1px solid ${B}`, borderRadius: 8, padding: "8px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none" }}>
          {[["all","All Plans"],["trial","Trial"],["free","Free"],["presence","Presence"],["authority","Authority"],["team","Team"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
          <thead>
            <tr>
              <th style={{ ...TH_S, cursor: "default" }}></th>
              {[["linkedin_name","Name / Email"],["plan","Plan"],["tone","Tone"],["post_count","Posts"],["trial_ends","Trial ends"],["created_at","Joined"]].map(([col, label]) => (
                <th key={col} style={TH_S} onClick={() => toggleSort(col)}>{label}{sortIndicator(col)}</th>
              ))}
              <th style={{ ...TH_S, cursor: "default" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ ...TD_S, textAlign: "center", color: "#999", padding: 32 }}>Loading…</td></tr>}
            {!loading && !filtered.length && <tr><td colSpan={9} style={{ ...TD_S, textAlign: "center", color: "#999", padding: 32 }}>No users found</td></tr>}
            {filtered.map(u => (
              <tr key={u.id} className="ar" style={{ cursor: "pointer" }} onClick={() => onNavigate("user-detail", u.id)}>
                <td style={{ ...TD_S, width: 40, paddingRight: 4 }}><Avatar name={u.linkedin_name || u.email} img={u.linkedin_profile_image} size={28} /></td>
                <td style={TD_S}><div style={{ fontWeight: 500 }}>{u.linkedin_name || "—"}</div><div style={{ color: "#999", fontSize: 12 }}>{u.email}</div></td>
                <td style={TD_S}><PlanBadge plan={u.plan} /></td>
                <td style={{ ...TD_S, color: "#666" }}>{u.tone || "—"}</td>
                <td style={{ ...TD_S, color: "#666" }}>{u.post_count || 0}</td>
                <td style={{ ...TD_S, color: "#666" }}>{u.trial_ends ? new Date(u.trial_ends).toLocaleDateString("en-CA") : "—"}</td>
                <td style={{ ...TD_S, color: "#999" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString("en-CA") : "—"}</td>
                <td style={TD_S}><button onClick={e => { e.stopPropagation(); onNavigate("user-detail", u.id); }} style={{ background: "#EBF5FB", color: G, border: "none", borderRadius: 6, padding: "5px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, cursor: "pointer" }}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── User detail ──────────────────────────────────────────────────────────────
function AdminUserDetail({ userId, token, onNavigate }) {
  const [user, setUser]         = useState(null);
  const [posts, setPosts]       = useState([]);
  const [health, setHealth]     = useState(null);
  const [loading, setLoad]      = useState(true);
  const [genLoad, setGenLoad]   = useState(false);
  const [genResult, setGenRes]  = useState(null);
  const [planEdit, setPlanEdit] = useState("trial");
  const [planSaving, setPlanS]  = useState(false);
  const [overrideLoad, setOvL]  = useState(false);
  const [toast, setToast]       = useState(null);
  const h = ah(token);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    fetch(`${API_BASE}/app/api/admin/user/${userId}`, { headers: h })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setUser(d.user); setPosts(d.posts || []); setPlanEdit(d.user?.plan || "trial"); } setLoad(false); })
      .catch(() => setLoad(false));
    fetch(`${API_BASE}/app/api/admin/user/${userId}/linkedin-health`, { headers: h })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHealth(d); })
      .catch(() => {});
  }, [userId]); // eslint-disable-line

  const generatePosts = async () => {
    setGenLoad(true); setGenRes(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/user/${userId}/generate-posts`, { method: "POST", headers: h });
      const d = await r.json();
      setGenRes(d.posts_generated != null ? `Generated ${d.posts_generated} posts ✓` : d.error || "Done");
    } catch { setGenRes("Request failed"); }
    setGenLoad(false);
  };

  const savePlan = async () => {
    setPlanS(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/user/${userId}/update-plan`, { method: "POST", headers: h, body: JSON.stringify({ plan: planEdit }) });
      if (r.ok) { setUser(u => ({ ...u, plan: planEdit })); showToast("Plan updated ✓"); }
    } catch { /* ignore */ }
    setPlanS(false);
  };

  const extendTrial = async () => {
    const newEnd = new Date(Date.now() + 7 * 86400000).toISOString();
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/user/${userId}/update-plan`, { method: "POST", headers: h, body: JSON.stringify({ trial_ends: newEnd }) });
      if (r.ok) { setUser(u => ({ ...u, trial_ends: newEnd })); showToast("Trial extended 7 days ✓"); }
    } catch { /* ignore */ }
  };

  const toggleOverride = async () => {
    const newVal = !(user?.admin_override);
    setOvL(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/admin/user/${userId}/admin-override`, { method: "POST", headers: h, body: JSON.stringify({ admin_override: newVal }) });
      if (r.ok) { setUser(u => ({ ...u, admin_override: newVal })); showToast(`Admin override ${newVal ? "enabled" : "disabled"} ✓`); }
    } catch { /* ignore */ }
    setOvL(false);
  };

  const fmtDate = d => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; } };

  const statusColor = s => ({ published: GRN, failed: RED, scheduled: "#7C3AED", approved: G, cancelled: "#E87840", pending: "#999" }[s] || "#999");

  const TD = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, padding: "9px 12px", borderBottom: `1px solid ${B}`, color: "#1A1A1A" };
  const TH = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", padding: "8px 12px", textAlign: "left", borderBottom: `1px solid ${B}`, fontWeight: 500 };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Loading…</div>;
  if (!user)   return <div style={{ padding: 40, textAlign: "center", color: RED,   fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>User not found</div>;

  const tokenData = health?.token || {};
  const profilePic = user.profile_picture_url || user.linkedin_profile_image;
  const fullName = user.linkedin_name || [user.first_name, user.last_name].filter(Boolean).join(" ") || "Unknown";

  return (
    <div className="af">
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", borderRadius: 10, padding: "12px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, zIndex: 2000, boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}>{toast}</div>
      )}

      <button onClick={() => onNavigate("users")} style={{ background: "none", border: "none", color: G, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, marginBottom: 20, padding: 0 }}>← Back to users</button>

      {/* User card */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Avatar name={fullName} img={profilePic} size={64} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 2 }}>{fullName}</div>
            {user.headline && <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#444", marginBottom: 4 }}>{user.headline}</div>}
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#666", marginBottom: 10 }}>{user.email}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <PlanBadge plan={user.plan} />
              <AccountStatusBadge status={user.account_status} />
              <span style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: user.linkedin_id ? GRN : RED }}>{user.linkedin_id ? "✓ LinkedIn connected" : "✗ LinkedIn disconnected"}</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
          {[
            ["Timezone",           user.timezone || "—"],
            ["Tone",               user.tone || "—"],
            ["Auto mode",          user.auto_mode ? "On" : "Off"],
            ["AI rewrites today",  `${user.ai_rewrites_today || 0} / 25`],
            ["Trial ends",         fmtDate(user.trial_ends)],
            ["Joined",             fmtDate(user.created_at)],
            ["Industry",           user.industry || "—"],
            ["Followers",          user.follower_count != null ? user.follower_count.toLocaleString() : "—"],
            ["Last profile sync",  fmtDate(user.last_profile_sync)],
            ["Last post",          fmtDate(health?.last_post_at)],
            ["Total posts sent",   health?.total_posts != null ? health.total_posts : "—"],
          ].map(([k, v]) => (
            <div key={k} style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 3 }}>{k}</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* LinkedIn profile URL */}
        {user.linkedin_profile_url && (
          <div style={{ marginTop: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13 }}>
            <span style={{ color: "#999", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>LinkedIn Profile </span>
            <a href={user.linkedin_profile_url} target="_blank" rel="noreferrer" style={{ color: G, textDecoration: "none" }}>{user.linkedin_profile_url}</a>
          </div>
        )}

        {/* LinkedIn email */}
        {user.linkedin_email && (
          <div style={{ marginTop: 8, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13 }}>
            <span style={{ color: "#999", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>LinkedIn Email </span>
            <span style={{ fontWeight: 500 }}>{user.linkedin_email}</span>
          </div>
        )}

        {/* Token health */}
        {(tokenData.expires_at || tokenData.needs_reauth) && (
          <div style={{ marginTop: 14, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13 }}>
            <span style={{ color: "#999", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>Token </span>
            <TokenStatusBadge expiresAt={tokenData.expires_at} needsReauth={tokenData.needs_reauth} />
            {tokenData.expires_at && <span style={{ color: "#666" }}>Expires {fmtDate(tokenData.expires_at)}</span>}
          </div>
        )}

        {/* admin_override toggle */}
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13 }}>
          <span style={{ color: "#666" }}>Admin Override:</span>
          <button onClick={toggleOverride} disabled={overrideLoad}
            style={{ background: user.admin_override ? GRN : "#F3F4F6", color: user.admin_override ? "#fff" : "#374151", border: `1px solid ${user.admin_override ? GRN : "#D1D5DB"}`, borderRadius: 6, padding: "5px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, cursor: overrideLoad ? "not-allowed" : "pointer", fontWeight: 600 }}>
            {overrideLoad ? "…" : user.admin_override ? "ON — disable" : "OFF — enable"}
          </button>
          <span style={{ fontSize: 12, color: "#999" }}>Bypasses trial/churn checks on OAuth</span>
        </div>

        {user.topics && user.topics.length > 0 && (
          <div style={{ marginTop: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Topics</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {user.topics.map((t, i) => <span key={i} style={{ background: "#EBF5FB", color: G, borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>{t}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "18px 20px", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={generatePosts} disabled={genLoad}
          style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: genLoad ? "not-allowed" : "pointer", opacity: genLoad ? 0.7 : 1, whiteSpace: "nowrap" }}>
          {genLoad ? "Generating…" : "Generate Posts Now"}
        </button>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={planEdit} onChange={e => setPlanEdit(e.target.value)}
            style={{ border: `1px solid ${B}`, borderRadius: 8, padding: "8px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, outline: "none" }}>
            {[["trial","Trial"],["free","Free"],["presence","Presence"],["authority","Authority"],["team","Team"]].map(([p,l]) => <option key={p} value={p}>{l}</option>)}
          </select>
          <button onClick={savePlan} disabled={planSaving || planEdit === user.plan}
            style={{ background: planEdit !== user.plan ? G : "#EBF5FB", color: planEdit !== user.plan ? "#fff" : "#999", border: "none", borderRadius: 8, padding: "9px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: planEdit !== user.plan ? "pointer" : "not-allowed" }}>
            {planSaving ? "Saving…" : "Change Plan"}
          </button>
        </div>

        <button onClick={extendTrial}
          style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: 8, padding: "9px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
          Extend Trial 7 Days
        </button>

        <button onClick={() => onNavigate("impersonate", userId)}
          style={{ background: "#F5F3FF", color: "#6D28D9", border: "1px solid #C4B5FD", borderRadius: 8, padding: "9px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
          View as User
        </button>

        {genResult && <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: genResult.includes("✓") ? GRN : RED }}>{genResult}</div>}
      </div>

      {/* Posts table */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${B}` }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18 }}>Recent Posts ({posts.length})</div>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Scheduled for","Caption","Status","Type"].map((h,i) => <th key={i} style={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {!posts.length && <tr><td colSpan={4} style={{ ...TD, textAlign: "center", color: "#999", padding: 32 }}>No posts yet</td></tr>}
            {posts.map(p => (
              <tr key={p.id} className="ar">
                <td style={{ ...TD, whiteSpace: "nowrap" }}>{p.scheduled_for ? new Date(p.scheduled_for).toLocaleDateString("en-CA") : "—"}</td>
                <td style={{ ...TD, maxWidth: 260 }}><div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#444" }}>{(p.caption || "").slice(0, 80) || "—"}</div></td>
                <td style={TD}><span style={{ background: statusColor(p.status) + "22", color: statusColor(p.status), borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>{(p.status || "pending").toUpperCase()}</span></td>
                <td style={TD}>{p.is_custom && <span style={{ background: "#F5F3FF", color: "#6D28D9", borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 600 }}>CUSTOM</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Impersonate view ─────────────────────────────────────────────────────────
function AdminImpersonate({ userId, token, onExit }) {
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const h = ah(token);
    fetch(`${API_BASE}/app/api/admin/user/${userId}`, { headers: h })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.user) setUserName(d.user.linkedin_name || d.user.email || "this user"); })
      .catch(() => {});
  }, [userId]); // eslint-disable-line

  return (
    <div>
      <div style={{ background: "#FFFBEB", borderBottom: "2px solid #FCD34D", padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 300 }}>
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#92400E" }}>
          Admin View — Viewing <strong>{userName || "user"}</strong>'s dashboard. This is read-only.
        </div>
        <button onClick={onExit} style={{ background: "#92400E", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer" }}>Exit</button>
      </div>
      <div style={{ pointerEvents: "none", opacity: 0.95 }}>
        <NectarApp initialView="dashboard" />
      </div>
    </div>
  );
}

// ─── Error Log ────────────────────────────────────────────────────────────────
function AdminErrorLog({ token }) {
  const [data, setData]   = useState(null);
  const [load, setLoad]   = useState(true);
  const h = ah(token);

  useEffect(() => {
    fetch(`${API_BASE}/app/api/admin/error-log`, { headers: h })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoad(false); })
      .catch(() => setLoad(false));
  }, []); // eslint-disable-line

  const fmtDate = d => { try { return new Date(d).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  const typeColor = t => ({ busy_bee: "#F59E0B", timeout: "#EF4444", rate_limit: "#8B5CF6", auth_failure: "#6B7280", unknown: "#9CA3AF" })[t] || "#9CA3AF";

  if (load) return <div style={{ padding: 40, textAlign: "center", color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Loading…</div>;

  return (
    <div className="af">
      <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 24, marginBottom: 20 }}>Error Log</div>

      {/* Summary counts */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          ["Last 24h", data?.count_24h ?? "—"],
          ["Last 7 days", data?.count_7d ?? "—"],
        ].map(([label, val]) => (
          <div key={label} style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "16px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: val > 0 ? RED : GRN }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Error table */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Time", "Type", "Status", "Endpoint", "User ID"].map((h, i) => (
              <th key={i} style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", padding: "10px 14px", textAlign: "left", borderBottom: `1px solid ${B}`, fontWeight: 500 }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(!data?.errors?.length) && (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: "center", color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>No errors logged</td></tr>
            )}
            {(data?.errors || []).map((e, i) => (
              <tr key={i} className="ar">
                <td style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, padding: "9px 14px", borderBottom: `1px solid ${B}`, whiteSpace: "nowrap" }}>{fmtDate(e.created_at)}</td>
                <td style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, padding: "9px 14px", borderBottom: `1px solid ${B}` }}>
                  <span style={{ background: typeColor(e.error_type) + "22", color: typeColor(e.error_type), borderRadius: 4, padding: "2px 8px", fontWeight: 600, letterSpacing: "0.06em" }}>{(e.error_type || "unknown").toUpperCase()}</span>
                </td>
                <td style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, padding: "9px 14px", borderBottom: `1px solid ${B}`, color: e.status_code >= 500 ? RED : "#444" }}>{e.status_code || "—"}</td>
                <td style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, padding: "9px 14px", borderBottom: `1px solid ${B}`, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#555" }}>{e.endpoint || "—"}</td>
                <td style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, padding: "9px 14px", borderBottom: `1px solid ${B}`, color: "#666" }}>{e.user_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
